import React from 'react';
import { ReportingContainerBaseConfig } from '../../base';
import { ReportGenerateController, ReportGenerateLoader } from './reportHomeInterface';
import { ReportGenerateContainerProps, ReportGenerateViewProps } from './reportHomeProps';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { ReportHomeFormValues } from './reportHomeModel';
import { useNavigate } from 'react-router';
import { ContextItem } from '@varos/assistant-sdk';
import { ensureFileArray } from './reportHomeHelper';
import { assertNever } from '@varos/util-typescript';
import { useDisclosure } from '@chakra-ui/react';

// Define local type matching the expected schema until SDK is updated
type FilePayload = {
    filename: string;
    type: 'markdown' | 'csv';
    content: string;
};

export function createReportGenerateContainer(
    config: ReportingContainerBaseConfig,
    loader: ReportGenerateLoader,
    controller: ReportGenerateController,
    View: React.FC<ReportGenerateViewProps>
): React.FC<ReportGenerateContainerProps> {
    const {
        kernel: {
            infra: {
                optionParser: { useOptions },
            },
        },
        repository,
        context: { useContext },
        state: states,
    } = config;

    const MAX_POLLING_ATTEMPTS = 60;
    const POLLING_INTERVAL = 1000;

    return states.chat.subscribe(({ ...containerProps }) => {
        const context = useContext();
        const navigate = useNavigate();

        const mutation = {
            // task: {
            //     create: repository.platform.task.useCreate(context),
            // },
            content: {
                create: repository.research.content.useCreate(context),
            },
            thread: {
                create: repository.assistant.thread.useCreate(context),
                generate: repository.assistant.thread.useGenerate(context),
            },
            message: {
                create: repository.assistant.message.useCreate(context),
            },
        };

        const state = states.chat.useState(context);

        const data = loader.useLoad(context, {});

        const submitMutation = config.kernel.infra.repository.useMutation(context, {
            async mutationFn(props: { text: string }) {
                const suggestion = await mutation.thread.generate.mutateAsync({
                    prompt: props.text,
                });
                console.log('threadsuggestion', suggestion);

                console.log('creating content');
                const response = await mutation.content.create.mutateAsync({
                    type: 'document',
                    title: suggestion.title,
                    icon: suggestion.emoji,
                    blocks: [
                        {
                            kind: 'callout',
                            text: props.text,
                            icon: '❓',
                        },
                    ],
                });
                console.log('content created', response);

                console.log('creating thread');
                const thread = await mutation.thread.create.mutateAsync({
                    title: suggestion.title,
                    context: [
                        ...suggestion.context,
                        {
                            kind: 'root',
                            document: response.id,
                        },
                    ],
                });
                console.log('thread created', thread);

                console.log('creating message');
                const message = await mutation.message.create.mutateAsync({
                    thread: thread.id,
                    parts: [
                        {
                            type: 'text',
                            text: props.text,
                        },
                    ],
                    context: thread.context.map((item): ContextItem => {
                        if (item.kind === 'prompt') {
                            return {
                                kind: item.kind,
                                data: {
                                    text: item.prompt,
                                },
                            };
                        }
                        if (item.kind === 'root' || item.kind === 'reference') {
                            return {
                                kind: item.kind,
                                data: {
                                    document: item.document,
                                },
                            };
                        }
                        if (item.kind === 'task') {
                            return {
                                kind: item.kind,
                                data: {
                                    goal: item.goal,
                                    steps: item.steps,
                                },
                            };
                        }
                        assertNever(item);
                    }),
                });
                console.log('message created', message);

                console.log('setting active thread', thread.id);
                state.onChange({
                    thread: thread.id,
                    isOpen: true,
                });

                console.log('navigating to new content', response.id);
                navigate(`/reporting/reports/${response.id}?expand=${response.id}`);
            },
            onSuccess(data) {
                console.log('success', data);
            },
        });

        const form = useForm<ReportHomeFormValues>({
            defaultValues: {
                question: '',
                files: [],
            },
        });

        const onSubmit: SubmitHandler<ReportHomeFormValues> = async (values) => {
            console.log('Submitting:', values);

            // const browserFiles = ensureFileArray(values.files);
            // console.log('Selected files (as array):', browserFiles);

            // const filesPayload: FilePayload[] = await Promise.all(
            //     browserFiles.map(async (file) => {
            //         console.log('file.type', file.type);
            //         const content = await file.text();
            //         return {
            //             filename: file.name,
            //             type: file.type === 'text/csv' ? 'csv' : 'markdown',
            //             content: content,
            //         };
            //     })
            // );

            // const payload: TaskCreatePayload = {
            //     kind: 'report',
            //     input: {
            //         text: values.question,
            //         files: filesPayload.length > 0 ? filesPayload : undefined,
            //     },
            // };

            const response = await submitMutation.mutateAsync({
                text: values.question,
            });
            form.reset();
        };

        const props = controller.useProps(context, data, {
            values: form.watch(),
            form: {
                id: 'question',
                form,
                onSubmit: onSubmit,
            },
            disclosure: useDisclosure(),
        });
        return <View {...props} />;
    });
}
